import { 
	getAudioPlayerElement, 
	getAudioPlayerCollapsed, 
	getCurrentAudioFile,
	getAudioPlayerState,
	setAudioPlayerState
} from './player-core.js';
import { animatePlayerCollapse } from './player-ui.js';
import { showAudioPlayer } from './player-core.js';

// Toggle button state
let clickTimeout = null; // Track click timeout to prevent single click when double-clicking
let volumeDisplayTimeout = null; // Track seek display timeout (reused variable name for compatibility)
let wheelDebounceTimeout = null; // Track wheel debounce timeout
let virtualSeekTime = null; // Track virtual seek time for immediate display
let playerObserver = null; // Track MutationObserver for player visibility

export function showAudioPlayerToggle() {
	const toggleButton = $('#audio-player-toggle');
	if (toggleButton.length) {
		toggleButton.addClass('audio-player-toggle--visible');
		// Don't add 'active' class here - let updateToggleButton handle it
		// This ensures paused state is checked correctly
		
		// Create volume display overlay if it doesn't exist
		if (!toggleButton.find('.audio-player-toggle__volume-display').length) {
			const volumeDisplay = $('<div class="audio-player-toggle__volume-display"></div>');
			toggleButton.append(volumeDisplay);
		}
		
		// Update filename display
		const filenameElement = $('#audio-player-toggle-filename');
		if (filenameElement.length) {
			const currentAudioFile = getCurrentAudioFile();
			if (currentAudioFile && currentAudioFile.file) {
				const fileName = currentAudioFile.file.n || currentAudioFile.file.name || '';
				// Remove extension (e.g., .mp3, .MP3)
				const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
				if (fileNameWithoutExt) {
					filenameElement.text(fileNameWithoutExt).addClass('audio-player-toggle__filename--visible');
				}
			}
		}
	}
}

export function hideAudioPlayerToggle() {
	const toggleButton = $('#audio-player-toggle');
	if (toggleButton.length) {
		toggleButton.removeClass('audio-player-toggle--visible');
		toggleButton.removeClass('active');
		toggleButton.removeClass('audio-player-toggle--paused');
	}
	// Hide filename when toggle is hidden
	const filenameElement = $('#audio-player-toggle-filename');
	if (filenameElement.length) {
		filenameElement.removeClass('audio-player-toggle__filename--visible').text('');
	}
}

export function showPlayPauseIcon(action, toggleButton) {
	let iconDisplay = toggleButton.find('.audio-player-toggle__icon-display');
	if (!iconDisplay.length) {
		const iconDisplayEl = $('<div class="audio-player-toggle__icon-display"></div>');
		toggleButton.append(iconDisplayEl);
		iconDisplay = toggleButton.find('.audio-player-toggle__icon-display');
	}
	
	const iconClass = action === 'play' ? 'bi-play-fill' : 'bi-pause-fill';
	iconDisplay.html(`<i class="bi ${iconClass}"></i>`).addClass('audio-player-toggle__icon-display--visible');
	
	// Clear existing timeout
	if (volumeDisplayTimeout) {
		clearTimeout(volumeDisplayTimeout);
		volumeDisplayTimeout = null;
	}
	
	// Hide icon display after 800ms
	volumeDisplayTimeout = setTimeout(function() {
		iconDisplay.removeClass('audio-player-toggle__icon-display--visible');
		volumeDisplayTimeout = null;
	}, 800);
	
	// Visual feedback
	toggleButton.addClass('audio-player-toggle--media-action');
	setTimeout(function() {
		toggleButton.removeClass('audio-player-toggle--media-action');
	}, 300);
}

export function initAudioPlayerToggle() {
	const toggleButton = $('#audio-player-toggle');
	if (!toggleButton.length) return;
	
	// Create seek display overlay if it doesn't exist
	if (!toggleButton.find('.audio-player-toggle__seek-display').length) {
		const seekDisplay = $('<div class="audio-player-toggle__seek-display"></div>');
		toggleButton.append(seekDisplay);
	}
	
	// Create icon display overlay if it doesn't exist
	if (!toggleButton.find('.audio-player-toggle__icon-display').length) {
		const iconDisplay = $('<div class="audio-player-toggle__icon-display"></div>');
		toggleButton.append(iconDisplay);
	}
	
	// Hide button by default
	hideAudioPlayerToggle();
	
	// Observer callbacks for animatePlayerCollapse
	const observerCallbacks = {
		disconnectObserver: () => {
			if (playerObserver) {
				playerObserver.disconnect();
			}
		},
		reconnectObserver: () => {
			const audioPlayerElement = getAudioPlayerElement();
			if (audioPlayerElement && audioPlayerElement.length) {
				playerObserver = new MutationObserver(() => {
					const audioPlayerCollapsed = getAudioPlayerCollapsed();
					if (!audioPlayerCollapsed || audioPlayerElement.hasClass('audio-player-overlay--visible')) {
						updateToggleButton();
					}
				});
				playerObserver.observe(audioPlayerElement[0], {
					attributes: true,
					attributeFilter: ['class']
				});
			}
		}
	};
	
	// Update button state based on player visibility and collapsed state
	const updateToggleButton = () => {
		const audioPlayerElement = getAudioPlayerElement();
		const audioPlayerCollapsed = getAudioPlayerCollapsed();
		const currentAudioFile = getCurrentAudioFile();
		
		// If no current audio file, hide button (player was closed)
		if (!currentAudioFile || !currentAudioFile.file) {
			hideAudioPlayerToggle();
			return;
		}
		
		if (audioPlayerElement && audioPlayerElement.length) {
			// Update filename display
			updateFilenameDisplay();
			
			// CRITICAL: If player is collapsed, NEVER add 'audio-player-overlay--visible' class
			if (audioPlayerCollapsed) {
				// Player is minimized (collapsed) - show button AND make it active (filled)
				// IMPORTANT: Do NOT re-show the player here - it's intentionally collapsed
				toggleButton.addClass('audio-player-toggle--visible');
				toggleButton.addClass('active');
				
				// Check if audio is paused - if so, add paused state class
				const audio = audioPlayerElement.find('audio');
				if (audio.length > 0 && audio[0]) {
					const audioEl = audio[0];
					if (audioEl.paused) {
						toggleButton.addClass('audio-player-toggle--paused');
					} else {
						toggleButton.removeClass('audio-player-toggle--paused');
					}
				} else {
					// If audio element doesn't exist yet, assume paused (default state)
					toggleButton.addClass('audio-player-toggle--paused');
				}
				
				// Ensure class is NOT present
				audioPlayerElement.removeClass('audio-player-overlay--visible');
				return; // Exit early to prevent any other logic
			}
			
			if (audioPlayerElement.hasClass('audio-player-overlay--visible')) {
				// Player is visible - show button but NOT active (not filled)
				toggleButton.addClass('audio-player-toggle--visible');
				toggleButton.removeClass('active');
				toggleButton.removeClass('audio-player-toggle--paused');
			} else {
				// Player is hidden - hide button
				hideAudioPlayerToggle();
				// Hide filename when player is hidden
				hideFilenameDisplay();
			}
		} else {
			// Player doesn't exist - hide button
			hideAudioPlayerToggle();
			// Hide filename when player doesn't exist
			hideFilenameDisplay();
		}
	};
	
	// Update filename display next to toggle button
	const updateFilenameDisplay = () => {
		const filenameElement = $('#audio-player-toggle-filename');
		if (!filenameElement.length) return;
		
		const currentAudioFile = getCurrentAudioFile();
		if (currentAudioFile && currentAudioFile.file) {
			const fileName = currentAudioFile.file.n || currentAudioFile.file.name || '';
			// Remove extension (e.g., .mp3, .MP3)
			const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
			if (fileNameWithoutExt) {
				filenameElement.text(fileNameWithoutExt).addClass('audio-player-toggle__filename--visible');
			} else {
				hideFilenameDisplay();
			}
		} else {
			hideFilenameDisplay();
		}
	};
	
	// Hide filename display
	const hideFilenameDisplay = () => {
		const filenameElement = $('#audio-player-toggle-filename');
		if (filenameElement.length) {
			filenameElement.removeClass('audio-player-toggle__filename--visible').text('');
		}
	};
	
	// Initial state
	updateToggleButton();
	
	// Single click to play/pause audio
	toggleButton.off('click.audio-player-toggle').on('click.audio-player-toggle', function(e) {
		e.preventDefault();
		e.stopPropagation();
		
		// Clear any existing timeout
		if (clickTimeout) {
			clearTimeout(clickTimeout);
			clickTimeout = null;
		}
		
		// Delay single click to allow double-click detection
		clickTimeout = setTimeout(function() {
			clickTimeout = null;
			
			// Get audio element from player
			const audioPlayerElement = getAudioPlayerElement();
			if (audioPlayerElement && audioPlayerElement.length) {
				const audio = audioPlayerElement.find('audio');
				if (audio.length > 0 && audio[0]) {
					const audioEl = audio[0];
					
					// Add visual feedback
					toggleButton.addClass('audio-player-toggle--clicked');
					setTimeout(function() {
						toggleButton.removeClass('audio-player-toggle--clicked');
					}, 300);
					
					// Show icon before play/pause
					const willPlay = audioEl.paused;
					if (willPlay) {
						showPlayPauseIcon('play', toggleButton);
						audioEl.play().catch(err => {
							console.warn('[AudioPlayer] Play failed:', err);
						});
					} else {
						showPlayPauseIcon('pause', toggleButton);
						audioEl.pause();
					}
					
					// Update toggle button state after play/pause (to update paused class)
					// Use a small delay to ensure audio state has updated
					setTimeout(() => {
						updateToggleButton();
					}, 50);
				}
			}
		}, 300); // 300ms delay to detect double-click
	});
	
	// Double-click to toggle player visibility/collapsed state
	toggleButton.off('dblclick.audio-player-toggle').on('dblclick.audio-player-toggle', function(e) {
		e.preventDefault();
		e.stopPropagation();
		
		// Cancel single click timeout
		if (clickTimeout) {
			clearTimeout(clickTimeout);
			clickTimeout = null;
		}
		
		const audioPlayerElement = getAudioPlayerElement();
		const audioPlayerCollapsed = getAudioPlayerCollapsed();
		
		if (audioPlayerElement && audioPlayerElement.length && audioPlayerElement.hasClass('audio-player-overlay--visible')) {
			// Player is visible - minimize it (hide and show icon as active)
			animatePlayerCollapse(true, observerCallbacks);
		} else if (audioPlayerElement && audioPlayerElement.length && audioPlayerCollapsed) {
			// Player is minimized (collapsed) - restore it
			animatePlayerCollapse(false, observerCallbacks);
		} else {
			// Player is hidden - show it if we have a current file
			const currentAudioFile = getCurrentAudioFile();
			if (currentAudioFile && currentAudioFile.file && currentAudioFile.fileKey) {
				showAudioPlayer(currentAudioFile.file, currentAudioFile.fileKey);
			}
		}
		updateToggleButton();
	});
	
	// Wheel scroll to seek forward/backward when hovering over toggle button
	if (toggleButton && toggleButton[0]) {
		toggleButton[0].addEventListener('wheel', function(e) {
			// e.preventDefault(); // Removed to allow passive listener
			e.stopPropagation();
			
			// Get audio element from player
			const audioPlayerElement = getAudioPlayerElement();
			if (audioPlayerElement && audioPlayerElement.length) {
				const audio = audioPlayerElement.find('audio');
				if (audio.length > 0 && audio[0]) {
					const audioEl = audio[0];
					if (!audioEl.duration) return; // Audio not loaded yet
					
					// Using native event delta instead of originalEvent
					const delta = e.deltaY || 0;
					const seekStep = 5; // 5 seconds per scroll step
					
					// Initialize virtual seek time if not set (use current audio time as base)
					if (virtualSeekTime === null) {
						virtualSeekTime = audioEl.currentTime;
					}
					
					// Calculate immediate seek change for display (without debounce)
					// Normal: scroll down (delta > 0) = backward, scroll up (delta < 0) = forward
					const immediateSeekChange = delta > 0 ? -seekStep : seekStep;
					virtualSeekTime = Math.max(0, Math.min(audioEl.duration, virtualSeekTime + immediateSeekChange));
					
					// Update display immediately (no debounce) - show virtual time
					updateSeekDisplay(virtualSeekTime, toggleButton);
					
					// Clear existing debounce timeout for audio seek
					if (wheelDebounceTimeout) {
						clearTimeout(wheelDebounceTimeout);
						wheelDebounceTimeout = null;
					}
					
					// Debounce: apply actual audio seek after 500ms of no wheel events
					wheelDebounceTimeout = setTimeout(function() {
						// Use virtual seek time directly - it's what user sees on screen
						if (virtualSeekTime !== null) {
							const targetTime = Math.max(0, Math.min(audioEl.duration, virtualSeekTime));
							const currentTime = audioEl.currentTime;
							
							// Only apply if there's a significant difference
							if (Math.abs(targetTime - currentTime) > 0.1) {
								audioEl.currentTime = targetTime;
							}
							
							// Update display with final time (in case it changed)
							updateSeekDisplay(targetTime, toggleButton);
						}
						
						// Reset virtual time
						virtualSeekTime = null;
						wheelDebounceTimeout = null;
					}, 500); // 500ms debounce delay for audio seek
				}
			}
		}, { passive: true });
	}
	
	// Helper function to update seek display (no debounce - updates immediately)
	function updateSeekDisplay(newTime, toggleButton) {
		// Format time display (MM:SS or HH:MM:SS)
		const formatTime = (seconds) => {
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			const secs = Math.floor(seconds % 60);
			
			if (hours > 0) {
				return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
			}
			return `${minutes}:${String(secs).padStart(2, '0')}`;
		};
		
		// Show seek display with current time
		const seekDisplay = toggleButton.find('.audio-player-toggle__seek-display');
		if (seekDisplay.length) {
			const timeText = formatTime(newTime);
			seekDisplay.text(timeText).addClass('audio-player-toggle__seek-display--visible');
			
			// Auto-adjust font size to fit container
			const adjustFontSize = () => {
				const container = seekDisplay[0];
				const containerWidth = container.offsetWidth;
				const containerHeight = container.offsetHeight;
				
				if (containerWidth === 0 || containerHeight === 0) {
					setTimeout(adjustFontSize, 10);
					return;
				}
				
				let fontSize = 2; // rem
				seekDisplay.css('font-size', fontSize + 'rem');
				
				const tempSpan = $('<span>').text(timeText).css({
					position: 'absolute',
					visibility: 'hidden',
					whiteSpace: 'nowrap',
					fontFamily: "'PT Sans Narrow', sans-serif",
					fontWeight: '800',
					fontSize: fontSize + 'rem',
					lineHeight: '1'
				});
				$('body').append(tempSpan);
				
				const textWidth = tempSpan[0].offsetWidth;
				const textHeight = tempSpan[0].offsetHeight;
				tempSpan.remove();
				
				const maxWidth = containerWidth * 0.95;
				const maxHeight = containerHeight * 0.95;
				
				if (textWidth > maxWidth || textHeight > maxHeight) {
					const widthRatio = maxWidth / textWidth;
					const heightRatio = maxHeight / textHeight;
					const ratio = Math.min(widthRatio, heightRatio);
					fontSize = fontSize * ratio;
					seekDisplay.css('font-size', Math.max(1, fontSize) + 'rem');
				}
			};
			
			setTimeout(adjustFontSize, 10);
			
			if (volumeDisplayTimeout) {
				clearTimeout(volumeDisplayTimeout);
				volumeDisplayTimeout = null;
			}
			
			volumeDisplayTimeout = setTimeout(function() {
				seekDisplay.removeClass('audio-player-toggle__seek-display--visible');
				volumeDisplayTimeout = null;
			}, 1000);
		}
		
		// Visual feedback
		toggleButton.addClass('audio-player-toggle--media-action');
		setTimeout(function() {
			toggleButton.removeClass('audio-player-toggle--media-action');
		}, 300);
	}
	
	// Watch for player visibility changes
	const audioPlayerElement = getAudioPlayerElement();
	if (audioPlayerElement && audioPlayerElement.length) {
		// Disconnect existing observer if any
		if (playerObserver) {
			playerObserver.disconnect();
		}
		
		playerObserver = new MutationObserver(() => {
			// Only update if not in the middle of collapse animation
			const audioPlayerCollapsed = getAudioPlayerCollapsed();
			// CRITICAL: Never re-add 'audio-player-overlay--visible' class if player is collapsed
			if (!audioPlayerCollapsed || audioPlayerElement.hasClass('audio-player-overlay--visible')) {
				updateToggleButton();
			}
		});
		playerObserver.observe(audioPlayerElement[0], {
			attributes: true,
			attributeFilter: ['class']
		});
		
		// Watch for play/pause events to update button state (especially paused state when collapsed)
		const audio = audioPlayerElement.find('audio');
		if (audio.length > 0 && audio[0]) {
			const audioEl = audio[0];
			
			// Remove existing listeners if any (to avoid duplicates)
			audioEl.removeEventListener('play', updateToggleButton);
			audioEl.removeEventListener('pause', updateToggleButton);
			
			// Add listeners for play/pause events
			audioEl.addEventListener('play', updateToggleButton);
			audioEl.addEventListener('pause', updateToggleButton);
		}
		
		// Also listen to custom events dispatched from player-ui.js (for when player is created dynamically)
		window.removeEventListener('audioPlayerPlay', updateToggleButton);
		window.removeEventListener('audioPlayerPause', updateToggleButton);
		window.addEventListener('audioPlayerPlay', updateToggleButton);
		window.addEventListener('audioPlayerPause', updateToggleButton);
	}
}

// Initialize toggle button on page load
if (typeof window !== 'undefined' && window.$) {
	$(document).ready(function() {
		initAudioPlayerToggle();
	});
}

export function toggleAudioPlayerCollapse() {
	const audioPlayerElement = getAudioPlayerElement();
	const audioPlayerCollapsed = getAudioPlayerCollapsed();
	if (!audioPlayerElement || !audioPlayerElement.length) return;
	
	const observerCallbacks = {
		disconnectObserver: () => {
			if (playerObserver) {
				playerObserver.disconnect();
			}
		},
		reconnectObserver: () => {
			if (audioPlayerElement && audioPlayerElement.length) {
				playerObserver = new MutationObserver(() => {
					const collapsed = getAudioPlayerCollapsed();
					if (!collapsed || audioPlayerElement.hasClass('audio-player-overlay--visible')) {
						// updateToggleButton will be called from initAudioPlayerToggle context
					}
				});
				playerObserver.observe(audioPlayerElement[0], {
					attributes: true,
					attributeFilter: ['class']
				});
			}
		}
	};
	
	animatePlayerCollapse(!audioPlayerCollapsed, observerCallbacks);
}

